import { useNavigate } from "react-router";
import { twJoin } from "tailwind-merge";

import type { DocumentsRootFolderDto } from "@/api/types";
import { ContextMenu, type ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { Icon54 } from "@/components/Icon54/Icon54";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { Trash02Icon } from "@/components/Icons/Trash02Icon";
import { cardSurfaceVariants } from "@/design-system/Card";
import { Info } from "@/design-system/Info";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { canManageAllDocuments } from "@/modules/documents/permissions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";
import { RuntimeHeight } from "@/ui-adapters/RuntimeHeight";

interface RootFolderProps {
  folder: DocumentsRootFolderDto;
  onDelete: (id: string) => void;
}

export function RootFolder({ folder, onDelete }: RootFolderProps): React.ReactNode {
  const slug = useSlug();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const hasPermission = usePermission();

  const menuActions: ContextMenuItem[] = [
    {
      labels: {
        default: t("common.action.edit"),
      },
      icon: <Edit05Icon />,
      "data-testid": "context-menu-edit-btn",
      value: "edit",
      callbackFn: () => {
        void navigate(routes.documents.rootEdit({ slug, folderId: folder.id }));
      },
      isHidden: !hasPermission(canManageAllDocuments),
    },
    {
      labels: {
        default: t("common.action.delete"),
        disabled: t("model.document.folder.action.delete.not-allowed"),
      },
      icon: <Trash02Icon />,
      "data-testid": "context-menu-delete-btn",
      value: "delete",
      callbackFn: () => onDelete(folder.id),
      isHidden: !hasPermission(canManageAllDocuments),
      isDisabled: !folder.isEmpty,
    },
  ];

  const handleNavigateFolder = () => {
    if (folder.isEmpty && !folder.canAddItem) {
      return;
    }

    void navigate(routes.documents.documentsList({ slug, folderId: folder.id }));
  };

  return (
    // Mocked "<a/>" used to allow interactive elements inside without propagating the click event
    <RuntimeHeight height={128}>
      {(heightAttributes) => (
        <article
          {...heightAttributes}
          className={twJoin(
            "flex flex-col items-center justify-center gap-space-md px-space-md",
            cardSurfaceVariants({ interactive: true }),
            folder.isEmpty && !folder.canAddItem ? "pointer-events-none opacity-50" : "cursor-pointer",
          )}
          role="link"
          tabIndex={0}
          aria-label={`Open "${folder.name}" folder`}
          onClick={handleNavigateFolder}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              handleNavigateFolder();
            }
          }}
          data-testid="documents-root-item"
        >
          <section className="relative flex w-full items-start gap-space-sm @md:gap-space-md">
            <span className="flex rounded-lg p-space-md ring-1 ring-border-default">
              <Icon54 char={folder.iconChar} />
            </span>
            <section className="flex flex-1 items-start justify-between gap-space-md">
              <Stack gap="xs">
                <h2 className="line-clamp-1 text-body-emphasis">{folder.name}</h2>
                {folder.description && (
                  <p className="line-clamp-2 text-caption text-text-secondary">{folder.description}</p>
                )}
                {folder.isEmpty && folder.canAddItem && (
                  <Inline gap="sm" align="center" data-testid="documents-root-item-empty">
                    <span className="text-overline text-text-secondary">{t("page.document.root.list.disabled")}</span>
                    <Info tooltip={t("page.document.root.list.disabled.description")} />
                  </Inline>
                )}
              </Stack>
              <ContextMenu items={menuActions} />
            </section>
          </section>
        </article>
      )}
    </RuntimeHeight>
  );
}
